import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { LoadingScreen } from "@/components/LoadingScreen";
import { RequireAuth } from "@/hooks/useAuthGuard";
import { AppShell } from "@/components/AppShell";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { SubscriptionProvider } from "@/context/SubscriptionContext";

// Public / auth funnel — flat routes
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Public digital-card page — lazy (no auth needed)
const PublicCardPage = lazy(() => import("@/pages/PublicCardPage"));
// Public assessment runner (Phase 4) — lazy
const PublicAssessmentPage = lazy(() => import("@/pages/PublicAssessmentPage"));
// Public lead magnet landing (Phase 5) — lazy
const PublicLeadMagnetPage = lazy(() => import("@/pages/PublicLeadMagnetPage"));

// Authed shell pages — nested under <AppShell>
const HomePage = lazy(() => import("@/pages/HomePage"));
const LeadsPlaceholder = lazy(() => import("@/pages/LeadsPlaceholder"));
const GrowPlaceholder = lazy(() => import("@/pages/GrowPage")); // variable name retained; module now renders the real Growth Hub per FAT #3
const MorePage = lazy(() => import("@/pages/MorePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));

// Digital Business Card — Phase 3 (lazy so the ~250KB QR + card renderer
// stays out of the main bundle for users who never open it).
const CardPage = lazy(() => import("@/pages/CardPage"));
const CardEditorPage = lazy(() => import("@/pages/CardEditorPage"));
const CardAnalyticsPage = lazy(() => import("@/pages/CardAnalyticsPage"));
const CardLeadsPage = lazy(() => import("@/pages/CardLeadsPage"));

// Assessments — Phase 4 (lazy)
const AssessmentsListPage = lazy(() => import("@/pages/AssessmentsListPage"));
const TemplatePickerPage = lazy(() => import("@/pages/TemplatePickerPage"));
const AssessmentDetailPage = lazy(() => import("@/pages/AssessmentDetailPage"));
const AssessmentAnalyticsPage = lazy(() => import("@/pages/AssessmentAnalyticsPage"));

// Lead Magnets — Phase 5 (lazy)
const LeadMagnetsListPage = lazy(() => import("@/pages/LeadMagnetsListPage"));
const LeadMagnetNewPage = lazy(() => import("@/pages/LeadMagnetNewPage"));
const LeadMagnetTemplatesPage = lazy(() => import("@/pages/LeadMagnetTemplatesPage"));
const LeadMagnetAIPage = lazy(() => import("@/pages/LeadMagnetAIPage"));
const LeadMagnetDetailPage = lazy(() => import("@/pages/LeadMagnetDetailPage"));

// Book a Discovery Call — FAT #3 (lazy)
const DiscoveryCallPage = lazy(() => import("@/pages/DiscoveryCallPage"));
const DiscoveryCallEditorPage = lazy(() => import("@/pages/DiscoveryCallEditorPage"));

// Leads CRM — Phase 6 (lazy)
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const LeadDetailPage = lazy(() => import("@/pages/LeadDetailPage"));
const FollowUpsPage = lazy(() => import("@/pages/FollowUpsPage"));
const MyNextBestActionsPage = lazy(() => import("@/pages/MyNextBestActionsPage"));
const GrowthHubPage = lazy(() => import("@/pages/GrowthHubPage"));

// Reviews & Social Proof — FAT #6 (lazy)
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const ReviewInboxPage = lazy(() => import("@/pages/ReviewInboxPage"));
const PublicReviewSubmitPage = lazy(() => import("@/pages/PublicReviewSubmitPage"));
const LeadsNewPage = lazy(() =>
  import("@/pages/LeadsAddPages").then((m) => ({ default: m.LeadsNewPage }))
);
const LeadsPastePage = lazy(() =>
  import("@/pages/LeadsAddPages").then((m) => ({ default: m.LeadsPastePage }))
);
const LeadsImportPage = lazy(() =>
  import("@/pages/LeadsAddPages").then((m) => ({ default: m.LeadsImportPage }))
);

const ComingSoonPage = lazy(() =>
  import("@/components/ComingSoonPage").then((m) => ({ default: m.ComingSoonPage }))
);

// FAT #18 — Subscription/membership engine
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const UpgradePlansPage = lazy(() =>
  import("@/pages/UpgradePage").then((m) => ({ default: m.UpgradePlansPage }))
);
const AdminSubscriptionsPage = lazy(() => import("@/pages/AdminSubscriptionsPage"));
const AdminPlaceholderPage = lazy(() => import("@/pages/AdminPlaceholderPage"));

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public / auth funnel */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage />} />
        <Route path="/verify/:token" element={<VerifyEmailPage />} />

        {/* Legal — always public */}
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
        <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />

        {/* FAT #18 — Upgrade / expired-beta screen. Authed but NOT gated. */}
        <Route
          path="/upgrade"
          element={
            <RequireAuth>
              <SubscriptionProvider>
                <UpgradePage />
              </SubscriptionProvider>
            </RequireAuth>
          }
        />
        <Route
          path="/upgrade/plans"
          element={
            <RequireAuth>
              <SubscriptionProvider>
                <UpgradePlansPage />
              </SubscriptionProvider>
            </RequireAuth>
          }
        />
        <Route
          path="/plans"
          element={
            <RequireAuth>
              <UpgradePlansPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/subscriptions"
          element={
            <RequireAuth>
              <SubscriptionProvider>
                <AdminSubscriptionsPage />
              </SubscriptionProvider>
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <SubscriptionProvider>
                <AdminPlaceholderPage />
              </SubscriptionProvider>
            </RequireAuth>
          }
        />

        {/*
         * Public Digital Business Card
         * ---------------------------------------------------------------
         * `/card/:username` is a public, unauthenticated surface.
         * React Router v6 ranks static segments above dynamic ones, so
         * the authed `/card`, `/card/editor`, `/card/analytics`, and
         * `/card/leads` below still win for their exact paths — this
         * dynamic route only catches everything else under /card/*.
         */}
        <Route path="/card/:username" element={<PublicCardPage />} />

        {/*
         * Public Win Lead Assessment runner
         * ---------------------------------------------------------------
         * `/take/:token` is a public, unauthenticated funnel: intro →
         * questions → capture → result. Same react-helmet-async OG
         * pattern as the public card.
         */}
        <Route path="/take/:token" element={<PublicAssessmentPage />} />

        {/*
         * Public review submission page — no auth. Renders the review form
         * from `_migration_source/frontend/app/review/[username].tsx` verbatim.
         */}
        <Route path="/review/:username" element={<PublicReviewSubmitPage />} />

        {/*
         * Public Lead Magnet landing page (Phase 5 crown jewel)
         * ---------------------------------------------------------------
         * `/m/:username/:slug` — public, unauthenticated. Value → Trust →
         * ONE Primary CTA → Subscriber. Same OG-meta pattern as public
         * card and public assessment.
         */}
        <Route path="/m/:username/:slug" element={<PublicLeadMagnetPage />} />

        {/* Authed app shell */}
        <Route
          element={
            <RequireAuth>
              <SubscriptionProvider>
                <SubscriptionGate>
                  <AppShell />
                </SubscriptionGate>
              </SubscriptionProvider>
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/new" element={<LeadsNewPage />} />
          <Route path="/leads/paste" element={<LeadsPastePage />} />
          <Route path="/leads/import" element={<LeadsImportPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/follow-ups" element={<FollowUpsPage />} />
          <Route path="/grow" element={<GrowPlaceholder />} />
          <Route path="/more" element={<MorePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/install" element={<InstallPage />} />

          {/* Digital Business Card — Phase 3 */}
          <Route path="/card" element={<CardPage />} />
          <Route path="/card/editor" element={<CardEditorPage />} />
          <Route path="/card/analytics" element={<CardAnalyticsPage />} />
          <Route path="/card/leads" element={<CardLeadsPage />} />

          {/* Win Lead Assessments — Phase 4 */}
          <Route path="/assessments" element={<AssessmentsListPage />} />
          <Route path="/assessments/new" element={<TemplatePickerPage />} />
          <Route path="/assessments/analytics" element={<AssessmentAnalyticsPage />} />
          <Route path="/assessments/:id" element={<AssessmentDetailPage />} />

          {/* Win Lead Conversion Campaign / Lead Magnets — Phase 5 */}
          <Route path="/lead-magnets" element={<LeadMagnetsListPage />} />
          <Route path="/lead-magnets/new" element={<LeadMagnetNewPage />} />
          <Route path="/lead-magnets/templates" element={<LeadMagnetTemplatesPage />} />
          <Route path="/lead-magnets/ai" element={<LeadMagnetAIPage />} />
          <Route path="/lead-magnets/:id" element={<LeadMagnetDetailPage />} />

          {/* Feature stubs — Coming Soon placeholders */}
          <Route
            path="/cost-monitor"
            element={
              <ComingSoonPage
                testid="cost-monitor-placeholder"
                title="Assessment Cost Monitor"
                phase="Phase 9/10 · Coming soon"
                description="Verify the assessment engine stays at $0 AI cost. Template-first report system keeps operations lean."
              />
            }
          />
          <Route
            path="/reviews"
            element={<ReviewsPage />}
          />
          <Route
            path="/review-inbox"
            element={<ReviewInboxPage />}
          />
          <Route
            path="/discovery-call"
            element={<DiscoveryCallPage />}
          />
          <Route
            path="/discovery-call/editor"
            element={<DiscoveryCallEditorPage />}
          />
          <Route path="/book-a-call" element={<Navigate to="/discovery-call" replace />} />
          <Route
            path="/growth-score"
            element={<MyNextBestActionsPage />}
          />
          <Route
            path="/my-next-best-actions"
            element={<MyNextBestActionsPage />}
          />
          <Route
            path="/growth-hub"
            element={<GrowthHubPage />}
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRouter;
